// ==============================================================================================
//  Subscription Locked Page
//  Displayed when a company's trial has expired or subscription is unpaid/locked.
//  Users can still access the subscription management page to reactivate.
// ==============================================================================================

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { Lock, CreditCard, LogOut, AlertTriangle } from 'lucide-react';

export default function SubscriptionLocked({ companyData }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const deleteDate = companyData?.deleteAt
    ? new Date(
        typeof companyData.deleteAt === 'string'
          ? companyData.deleteAt
          : companyData.deleteAt?.toDate
            ? companyData.deleteAt.toDate()
            : companyData.deleteAt
      ).toLocaleDateString()
    : null;

  const isTrialExpired = companyData?.subscriptionStatus === 'trial';
  const isLocked = companyData?.subscriptionStatus === 'locked';

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Lock Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
              <Lock className="w-10 h-10 text-red-600" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isLocked ? 'Account Locked' : 'Trial Expired'}
          </h1>

          <p className="text-gray-600 mb-6">
            {isLocked
              ? 'Your subscription has expired and your account is currently locked.'
              : 'Your free trial has ended. Subscribe to continue using Build My Checks.'}
          </p>

          {/* Warning about deletion */}
          {deleteDate && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-semibold text-amber-800">Data deletion scheduled</p>
                <p className="text-sm text-amber-700">
                  Your data will be permanently deleted on <strong>{deleteDate}</strong>. Subscribe before this date to keep your account.
                </p>
              </div>
            </div>
          )}

          {/* What's locked */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-gray-700 mb-2">While locked, you cannot:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-red-500">✕</span> Conduct new checks
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">✕</span> Create or edit forms
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">✕</span> Manage users or settings
              </li>
              <li className="flex items-center gap-2">
                <span className="text-red-500">✕</span> Access reports and submissions
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleSubscribe}
              className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              <CreditCard className="w-5 h-5" />
              Subscribe Now
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Log Out
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-6">
            Need help? Contact <a href="mailto:support@buildmychecks.co.uk" className="text-primary hover:underline">support@buildmychecks.co.uk</a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
// ----------------------------------------------------------------------------------------------
